import { useMutation, UseMutationOptions } from "@tanstack/react-query"
import { AxiosError } from "axios"
import { useTranslation } from "react-i18next"

import { httpClient } from "~/backend/axios.ts"
import { queryClient } from "~/backend/queryClient.ts"
import {
  CreateBusinessPartnerRequestDto,
  CreateBusinessPartnerResponseDto,
  findContactsByCreatedByIdQueryKey,
} from "~/gen/api"
import { BusinessPartnerKind } from "~/pages/Brand/Suppliers/SuppliersRepository/businessPartnerKind"
import { showAxiosError } from "~/utils/showAxiosError"
import { showSuccessNotification } from "~/utils/showSuccessNotification"

type CreateBusinessPartnerResponse = CreateBusinessPartnerResponseDto

export const useCreateContactMutation = (
  organizationId: string,
  options?: UseMutationOptions<
    CreateBusinessPartnerResponse,
    AxiosError,
    CreateBusinessPartnerRequestDto
  >
) => {
  const { t } = useTranslation("traceability")

  return useMutation({
    ...options,
    mutationFn: async (variables) =>
      (
        await httpClient.post<CreateBusinessPartnerResponse>(
          "v1/contacts/create-business-partner",
          variables
        )
      ).data,
    onSuccess: (data, variables, onMutateResult, context) => {
      options?.onSuccess?.(data, variables, onMutateResult, context)
      void queryClient.invalidateQueries({
        queryKey: findContactsByCreatedByIdQueryKey(organizationId),
      })
      showSuccessNotification({
        title:
          data.kind === BusinessPartnerKind.ON_BEHALF
            ? t("contactModal.messages.create.onBehalfSuccess")
            : t("contactModal.messages.create.success", {
                email: variables.email ?? "",
              }),
      })
      void queryClient.invalidateQueries({
        queryKey: [{ url: "/v1/client-settings/find-supplier-mapping" }],
      })
    },
    onError: (error, variables, onMutateResult, context) => {
      showAxiosError({
        notificationTitle: t("contactModal.messages.create.error", {
          error: error.response?.data ?? error.message,
        }),
        showNotification: true,
        error,
      })
      options?.onError?.(error, variables, onMutateResult, context)
    },
  })
}
